import type { ComponentProps, ReactNode } from "react"
import { motion } from "motion/react"
import { IconChevronRightOutline18 as ChevronRight } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"

// Every block in the thread right-bar shares one gutter, one label treatment,
// and one disclosure, so the column reads as a single list.
export const SIDEBAR_GROUP_PADDING = "px-2 py-4"
export const SIDEBAR_GROUP_GUTTER = "px-2"
// A 28px icon button insets its 14px glyph by 7px; pull that back so a trailing
// icon ends on the same right axis as the rows' badges and buttons.
export const SIDEBAR_TRAILING_ICON_PULL = "-mr-[7px]"

export function SidebarGroup({
	className,
	children,
	...props
}: ComponentProps<"div">) {
	return (
		<div className={cn(SIDEBAR_GROUP_PADDING, className)} {...props}>
			{children}
		</div>
	)
}

const LABEL_TEXT =
	"text-xs font-medium text-gray-500 select-none dark:text-gray-450"

export function SidebarGroupLabel({
	className,
	children,
	...props
}: ComponentProps<"div">) {
	return (
		<div
			className={cn(
				"mb-2 flex h-5 items-center",
				SIDEBAR_GROUP_GUTTER,
				LABEL_TEXT,
				className,
			)}
			{...props}
		>
			{children}
		</div>
	)
}

export function SidebarGroupToggle({
	label,
	isOpen,
	onToggle,
	trailing,
}: {
	label: ReactNode
	isOpen: boolean
	onToggle: () => void
	trailing?: ReactNode
}) {
	return (
		<div
			className={cn("mb-2 flex h-5 items-center gap-1", SIDEBAR_GROUP_GUTTER)}
		>
			<button
				type="button"
				onClick={onToggle}
				className={cn(
					"flex min-w-0 flex-1 items-center gap-0.5 text-left",
					LABEL_TEXT,
				)}
			>
				<span className="truncate">{label}</span>
				<motion.span
					animate={{ rotate: isOpen ? 90 : 0 }}
					transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
					className="flex h-3.5 w-3.5 flex-none items-center justify-center text-tertiary"
				>
					<ChevronRight className="h-3 w-3 flex-none" />
				</motion.span>
			</button>
			{trailing}
		</div>
	)
}
